import { MatchingProfileData, MatchingProvidersType, PsychAppointmentBookingSource } from '@uplift/api';
import { DateObject } from '@uplift/services';
import { InsuranceCompany, RecurringTypes } from '@uplift/types';
import React from 'react';

export type Step = 'start' | 'connect' | 'profile' | 'confirm';

export type PsychiatryMatchingContextData = {
  stateId: string;
  isDrawer: boolean;
  step: Step;
  patientName: string;
  patientId: string;
  providers: MatchingProfileData[];
  matchingProvidersType: MatchingProvidersType;
  onDone: (complete: boolean) => void;
  selectedProvider?: string;
  source?: PsychAppointmentBookingSource;
  appointment?: {
    providerUuid: string;
    patientUuid: string;
    fromDatetime: DateObject;
    recurring: RecurringTypes;
  };
  patientInsurance?: InsuranceCompany;
};

export type PsychiatryMatchingContextState = {
  state: PsychiatryMatchingContextData;
  dispatch: React.Dispatch<Action>;
};

const PsychiatryMatchingContext = React.createContext<PsychiatryMatchingContextState | null>(null);

type ChangeStepAction = {
  type: 'changeStep';
  payload: Step;
};

type ViewProviderProfileAction = {
  type: 'viewProviderProfile';
  payload: string;
};

type UpdateInsuranceAction = {
  type: 'updateInsurance';
  payload: InsuranceCompany;
};

type SetAppointmentAction = {
  type: 'setAppointment';
  payload: {
    fromDatetime: DateObject;
    providerId: string;
  };
};

type FinishAction = {
  type: 'finish';
  payload: boolean;
};

type Action =
  | ChangeStepAction
  | ViewProviderProfileAction
  | UpdateInsuranceAction
  | SetAppointmentAction
  | FinishAction;

type HandlerType = {
  [name: string]: (state: PsychiatryMatchingContextData, action: Action) => PsychiatryMatchingContextData;
};

const handlers: HandlerType = {
  changeStep: (state, action: ChangeStepAction) => ({
    ...state,
    step: action.payload
  }),
  viewProviderProfile: (state, action: ViewProviderProfileAction) => ({
    ...state,
    selectedProvider: action.payload,
    step: 'profile'
  }),
  updateInsurance: (state, action: UpdateInsuranceAction) => ({
    ...state,
    patientInsurance: action.payload
  }),
  setAppointment: (state, action: SetAppointmentAction) => ({
    ...state,
    appointment: {
      fromDatetime: action.payload.fromDatetime,
      recurring: RecurringTypes.none,
      patientUuid: state.patientId,
      providerUuid: action.payload.providerId,
    },
    step: 'confirm',
  }),
  finish: state => ({
    ...state
  })
};

const reducer = (state: PsychiatryMatchingContextData, action: Action): PsychiatryMatchingContextData =>
  handlers[action.type]
    ? handlers[action.type](state, action)
    : state;

export type PsychiatryMatchingContextContainerProps = {
  stateId: string;
  patientName: string;
  patientId: string;
  onDone: (complete: boolean) => void;
  children: React.ReactNode;
  providers?: MatchingProfileData[];
  matchingProvidersType?: MatchingProvidersType;
  isDrawer: boolean;
  source?: PsychAppointmentBookingSource;
};

const PsychiatryMatchingContextContainer_: React.FC<PsychiatryMatchingContextContainerProps> = ({ stateId, patientName, patientId, children, providers, onDone, isDrawer, source, matchingProvidersType }) => {
  const [ state, dispatch ] = React.useReducer(reducer, {
    stateId,
    patientName,
    patientId,
    matchingProvidersType,
    step: isDrawer ? 'start' : 'connect',
    providers,
    onDone: (complete) => {
      onDone(complete);
      dispatch({
        type: 'changeStep',
        payload: 'start'
      });
    },
    isDrawer,
    source,
  });

  const ctx = React.useMemo(() => ({ state, dispatch }), [ state, dispatch ]);


  return (
    <PsychiatryMatchingContext.Provider value={ctx}>
      {children}
    </PsychiatryMatchingContext.Provider>
  );
};

export const PsychiatryMatchingContextContainer = React.memo(PsychiatryMatchingContextContainer_);

export const usePsychiatryMatchingContext = () => React.useContext(PsychiatryMatchingContext);
